import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { GiftIcon, LockIcon, ScaleIcon, SendIcon, ShieldIcon, SplitIcon } from 'lucide-react';
import { DomainHeader } from '../../components/ui/DomainHeader';
import { Panel } from '../../components/ui/Panel';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Tabs } from '../../components/ui/Tabs';
import { Field, Input } from '../../components/ui/Field';
import { Meter } from '../../components/ui/Meter';
import { LedgerList } from '../../components/money/LedgerList';
import { BillsPanel } from '../../components/money/BillsPanel';
import { FeatureGate, FeatureStatusBadge, dependencyLabel, isLive } from '../../components/platform/FeatureStatusBadge';
import { featureRegister } from '../../data/platform';
import { capabilityGates, expenseSplits, moneyRequests, transactions } from '../../data/finance';
import { useDataMode } from '../../contexts/DataModeContext';
import { FirstRun } from '../../components/platform/FirstRun';
import { account } from '../../data/account';
import { deriveBalances } from '../../utils/ledger';
import { naira, percent } from '../../utils/format';
const TABS = ['Balances', 'Ledger', 'Send & split', 'Bills', 'Capabilities'] as const;

/** Read from the platform register, so this page cannot drift from it. */
const financialFeatures = featureRegister.filter(feature => feature.domain === 'Finance' || feature.domain === 'CampaGift');
export function Money() {
  const {
    isNew
  } = useDataMode();
  const [tab, setTab] = useState<(typeof TABS)[number]>('Balances');
  const [sendTo, setSendTo] = useState('');
  const [sendAmount, setSendAmount] = useState('');
  const balances = deriveBalances(transactions);
  if (isNew) {
    return <FirstRun surface="Money" what="A record of every amount that has moved, and what it was for." why="Rent instalments, marketplace orders, shared costs with housemates and CampaGift all report to the same ledger, so you can always trace a figure back to the thing that caused it." note="Campuzon does not hold your money. Every balance you will see here is worked out from the transaction record rather than stored as a number, and the capabilities that move real money stay switched off until the licensing and provider conditions behind them are met — each one names what it is waiting on." steps={[{
      label: 'Nothing has moved yet',
      detail: 'Your ledger is empty. That is the correct state for a new account, not a loading failure.'
    }, {
      label: 'It fills itself as you use Campuzon',
      detail: 'A housing application, a marketplace order or a gift received each create entries here automatically.',
      to: '/marketplace'
    }, {
      label: 'See what is available and what is not',
      detail: 'Transfers, bills, escrow and payouts are built and visibly gated. The register says which and why.'
    }]} data-id="element-10076" data-mp="2x6n9ptbs0j9n" />;
  }
  const balanceCards = [{
    key: 'available',
    label: 'Available',
    value: balances.available,
    note: 'Usable for permitted transactions'
  }, {
    key: 'pending',
    label: 'Pending',
    value: balances.pending,
    note: 'Unsettled — not spendable'
  }, {
    key: 'escrow',
    label: 'In escrow',
    value: balances.escrow,
    note: 'Committed to open marketplace orders'
  }, {
    key: 'withdrawable',
    label: 'Withdrawable',
    value: balances.withdrawable,
    note: 'Cleared settlement conditions'
  }];
  const incoming = moneyRequests.filter(request => request.direction === 'incoming' && request.state === 'awaiting');
  return <div className="space-y-6" data-id="element-10077" data-mp="3dq27olv0oas6">
      <DomainHeader domain="money" eyebrow="Commerce and financial core" title="Money" description="One transaction architecture behind housing, knowledge, services, events and bills — built on an immutable double-entry ledger." actions={<Badge tone="warn" icon={<LockIcon className="h-3.5 w-3.5" data-id="element-10080" data-mp="b3o74d7lp5xa" />} data-id="element-10079" data-mp="b4h6gt48ct5e">
            Regulated capabilities gated
          </Badge>} data-id="element-10078" data-mp="3dpaoybmiq7jb" />

      <div className="rounded-card border border-warn-100 bg-warn-50 px-5 py-4" data-id="element-10081" data-mp="64l9hdtya1zc">
        <p className="text-[13px] font-semibold text-ink" data-id="element-10082" data-mp="158e24p0jml5">
          Campuzon does not hold your funds or operate a wallet
        </p>
        <p className="mt-1 text-[13px] leading-6 text-ink-muted" data-id="element-10083" data-mp="3obskfm1dln2o">
          The figures below are internal records of entitlement derived from ledger entries. Funding,
          transfers, escrow holding, payouts and bill settlement are performed by licensed providers,
          and each remains switched off until Campuzon's regulatory position and provider onboarding
          are established.
        </p>
      </div>

      <Tabs tabs={TABS} value={tab} onChange={setTab} label="Money sections" data-id="element-10084" data-mp="1p5vl23u32vyk" />

      {tab === 'Balances' && <div className="space-y-6" data-id="element-10085" data-mp="1u2vumoq56guf">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-id="element-10086" data-mp="1u1u73krxw1vt">
            {balanceCards.map((card, index) => <div key={card.key} className={`rounded-card border bg-surface px-5 py-4 ${index === 0 ? 'border-brand-700' : 'border-line'}`} data-id="element-10087" data-mp="23tqj7zqv1sj1" data-mpi={index}>
                <p className="text-[12px] font-semibold uppercase tracking-wide text-ink-subtle" data-id="element-10088" data-mp="1b6dupk153an3">
                  {card.label}
                </p>
                <p className={`cz-num mt-2 font-semibold text-ink ${index === 0 ? 'text-3xl' : 'text-2xl'}`} data-id="element-10089" data-mp="1g6s4p7mawunr">
                  {naira(card.value)}
                </p>
                <p className="mt-1.5 text-[12px] leading-5 text-ink-muted" data-id="element-10090" data-mp="2whw20gyct7g8">{card.note}</p>
              </div>)}
          </div>

          <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]" data-id="element-10091" data-mp="wel61tsv6y0s">
            <Panel title="Recent activity" description="Every movement is a paired debit and credit. Expand a row to see its entries." bodyClassName="p-0" data-id="element-10092" data-mp="rgjm76nm6uo9">
              <LedgerList transactions={transactions.slice(0, 5)} data-id="element-10093" data-mp="11g0rhmyyf6jp" />
              <div className="border-t border-line px-5 py-3" data-id="element-10094" data-mp="dp53l0xmcvhq">
                <Button size="sm" variant="ghost" onClick={() => setTab('Ledger')} data-id="element-10095" data-mp="dnk5x2g0cwfn">
                  Open the full ledger
                </Button>
              </div>
            </Panel>
            <div className="space-y-6" data-id="element-10096" data-mp="mkcryb4kz7m5">
              <Panel title="Your identifiers" data-id="element-10097" data-mp="hr01rwk96sra">
                <dl className="space-y-3" data-id="element-10098" data-mp="hq8jfnfo2riy">
                  <div data-id="element-10099" data-mp="3q6tjgeqbma9x">
                    <dt className="text-[12px] text-ink-muted" data-id="element-10100" data-mp="3q6a2p1ceprxh">Public handle</dt>
                    <dd className="cz-num text-[14px] font-semibold text-ink" data-id="element-10101" data-mp="3m69jz4i9yukm">@{account.username}</dd>
                  </div>
                  <div data-id="element-10102" data-mp="3c11d7dm583wv">
                    <dt className="text-[12px] text-ink-muted" data-id="element-10103" data-mp="3bzq89agyoq46">Internal user id</dt>
                    <dd className="cz-num text-[13px] text-ink" data-id="element-10104" data-mp="3gu2s3lmco3gg">{account.id}</dd>
                  </div>
                  <div data-id="element-10105" data-mp="325gg9tvxlttz">
                    <dt className="text-[12px] text-ink-muted" data-id="element-10106" data-mp="321slysinx3e7">Financial identifier</dt>
                    <dd className="cz-num text-[13px] text-ink" data-id="element-10107" data-mp="370m1eqhif4h0">{account.financialIdentifier}</dd>
                  </div>
                </dl>
                <p className="mt-3 flex items-start gap-2 border-t border-line pt-3 text-[12px] leading-5 text-ink-muted" data-id="element-10108" data-mp="3mj9fo38jaj2">
                  <ShieldIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" data-id="element-10109" data-mp="3ktbm8rlay3cj" />
                  Your username is never the financial account identifier, and the financial
                  identifier is never shown in full or shared with a counterparty.
                </p>
              </Panel>
              <Panel title="CampaGift" description="A separate value instrument, not part of these balances." data-id="element-10110" data-mp="2ef3yzsesqkqq">
                <p className="text-[13px] leading-6 text-ink-muted" data-id="element-10111" data-mp="1qof4gjs6mio9">
                  Gift value is redeemable with participating merchants under its own rules. It is
                  recorded and reconciled separately from your money, escrow and library
                  entitlements, and cannot be spent as cash.
                </p>
                <Link to="/money/campagift" className="mt-3 inline-block" data-id="element-10112" data-mp="2ecyccy7bktft">
                  <Button size="sm" variant="secondary" icon={<GiftIcon className="h-4 w-4" data-id="element-10114" data-mp="29fgdk0n0nulj" />} data-id="element-10113" data-mp="29f6nkd1z1nfu">
                    Open CampaGift
                  </Button>
                </Link>
              </Panel>
              <Panel title="Escrow exposure" data-id="element-10115" data-mp="20n4nwgzq8b6m">
                <Meter label="Held against open orders" value={percent(balances.escrow, balances.escrow + balances.available)} trailing={naira(balances.escrow)} tone="warn" data-id="element-10116" data-mp="20m38y1flb4jm" />
                <p className="mt-3 text-[13px] leading-6 text-ink-muted" data-id="element-10117" data-mp="evwmln3l9fzl">
                  Reserved, pending and escrow funds cannot be spent. They clear only when the order
                  reaches Released or is reversed.
                </p>
                <Link to="/marketplace" className="mt-3 inline-block" data-id="element-10118" data-mp="nui37cpi2kxq">
                  <Button size="sm" variant="secondary" data-id="element-10119" data-mp="svp9f7bzvc2a">
                    View open orders
                  </Button>
                </Link>
              </Panel>
            </div>
          </div>
        </div>}

      {tab === 'Ledger' && <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_300px]" data-id="element-10120" data-mp="17jsg6ixv2rs5">
          <Panel title="Transaction history" bodyClassName="p-0" data-id="element-10121" data-mp="17klohocgxpce">
            <LedgerList transactions={transactions} data-id="element-10122" data-mp="1bkcazfuypjb4" />
          </Panel>
          <div className="space-y-6" data-id="element-10123" data-mp="1gkqnba3ljfey">
            <Panel title="Why history never changes" data-id="element-10124" data-mp="1glt9x5tvyvp2">
              <p className="flex items-start gap-2 text-[13px] leading-6 text-ink-muted" data-id="element-10125" data-mp="1ljbnxamrg13x">
                <ScaleIcon className="mt-0.5 h-4 w-4 shrink-0 text-ink-subtle" data-id="element-10126" data-mp="s6bm5wnsmj8x" />
                Ledger entries are immutable. A mistake is corrected by a reversal, adjustment or
                refund recorded beside the original, so balances, audits, disputes and reconciliation
                all reconstruct from the same authoritative history.
              </p>
            </Panel>
            <Panel title="Balances are derived" data-id="element-10127" data-mp="15zkqvk0sqgl4">
              <p className="text-[13px] leading-6 text-ink-muted" data-id="element-10128" data-mp="1avrn2l3e4dpv">
                Each balance shown on this page is computed from these entries. There is no mutable
                balance field anywhere in the architecture, and the interface is never the authority
                for a balance.
              </p>
            </Panel>
          </div>
        </div>}

      {tab === 'Send & split' && <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_340px]" data-id="element-10129" data-mp="3mnovykk9i47u">
          <div className="space-y-6" data-id="element-10130" data-mp="3qnfj8eaky27m">
            <Panel title="Send, request or gift" description="Addressed by @username, never by a financial identifier." data-id="element-10131" data-mp="3qoh4fblb8532">
              <div className="grid gap-4 sm:grid-cols-2" data-id="element-10132" data-mp="3j6iwvcmov42h">
                <Field label="Recipient" htmlFor="send-to" hint="Campuzon handle" data-id="element-10133" data-mp="3ecogai2dr57m">
                  <Input id="send-to" value={sendTo} onChange={event => setSendTo(event.target.value)} placeholder="@ifeoma.e" data-id="element-10134" data-mp="39dv5ixeh9sb5" />
                </Field>
                <Field label="Amount" htmlFor="send-amount" data-id="element-10135" data-mp="5vtobxn4f58b">
                  <Input id="send-amount" value={sendAmount} onChange={event => setSendAmount(event.target.value)} inputMode="numeric" placeholder="5000" data-id="element-10136" data-mp="zw77p419uuh" />
                </Field>
              </div>
              <div className="mt-4 flex flex-wrap gap-2 border-t border-line pt-4" data-id="element-10137" data-mp="2hp1o4wcca8zz">
                <Button variant="primary" disabled={!isLive('social_payments')} icon={<SendIcon className="h-3.5 w-3.5" data-id="element-10139" data-mp="2e6cgyl39k43x" />} data-id="element-10138" data-mp="2cnkt3hgjdgrb">
                  Send
                </Button>
                <Button variant="secondary" disabled={!isLive('social_payments')} data-id="element-10140" data-mp="2j29rubvrci9b">
                  Request
                </Button>
                <Link to="/money/campagift" data-id="element-10141" data-mp="295on7rnnh960">
                  <Button variant="secondary" icon={<GiftIcon className="h-4 w-4" data-id="element-10143" data-mp="1vfbf9pardugo" />} data-id="element-10142" data-mp="298j8tmieh010">
                    Gift
                  </Button>
                </Link>
                <Button variant="secondary" disabled={!isLive('social_payments')} icon={<SplitIcon className="h-3.5 w-3.5" data-id="element-10145" data-mp="1ligh57iedw2x" />} data-id="element-10144" data-mp="206thegoiq138">
                  Split
                </Button>
              </div>
              <div className="mt-4" data-id="element-10146" data-mp="1cgfnekmdhs60">
                <FeatureGate featureKey="social_payments" data-id="element-10147" data-mp="1chidiqbvfqkv" />
              </div>
            </Panel>

            <Panel title="Expense splits" bodyClassName="p-0" data-id="element-10148" data-mp="1hdz0vib8y0ww">
              <ul className="divide-y divide-line" data-id="element-10149" data-mp="xt8juzz7vwh5">
                {expenseSplits.map(split => {
              const settled = split.participants.filter(participant => participant.state === 'settled').length;
              return <li key={split.id} className="px-5 py-4" data-id="element-10150" data-mp="12sl7hmxwx7ks">
                      <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-10151" data-mp="12ra3bm0k1xt5">
                        <div className="min-w-0" data-id="element-10152" data-mp="1gqrwedkvvx8b">
                          <p className="text-[13px] font-semibold text-ink" data-id="element-10153" data-mp="1btjbi58cpf3d">{split.title}</p>
                          <p className="cz-num mt-0.5 text-[12px] text-ink-muted" data-id="element-10154" data-mp="1qi606xjvbj79">
                            {naira(split.totalNaira)} across {split.participants.length} people ·
                            created by @{split.createdBy} · {split.at}
                          </p>
                        </div>
                        <Badge tone={settled === split.participants.length ? 'ok' : 'warn'} data-id="element-10155" data-mp="1uk9gq0le9dze">
                          {settled}/{split.participants.length} settled
                        </Badge>
                      </div>
                      <ul className="mt-3 flex flex-wrap gap-2" data-id="element-10156" data-mp="3ty10fb1vz3j0">
                        {split.participants.map(participant => <li key={participant.handle} className="cz-num rounded-md border border-line px-2.5 py-1.5 text-[12px] text-ink-muted" data-id="element-10157" data-mp="2mhro0pfca19">
                            @{participant.handle} · {naira(participant.shareNaira)} ·{' '}
                            {participant.state === 'settled' ? 'settled' : 'awaiting'}
                          </li>)}
                      </ul>
                    </li>;
            })}
              </ul>
            </Panel>
          </div>

          <Panel title="Money requests" bodyClassName="p-0" data-id="element-10158" data-mp="udnifqj98vdv">
            <ul className="divide-y divide-line" data-id="element-10159" data-mp="z9mb3qbm8zk3">
              {moneyRequests.map(request => <li key={request.id} className="px-5 py-3.5" data-id="element-10160" data-mp="19d9i9d6kth34">
                  <div className="flex items-start justify-between gap-3" data-id="element-10161" data-mp="14frkmiy0eoaf">
                    <div className="min-w-0" data-id="element-10162" data-mp="14hu6cqs4pv9c">
                      <p className="text-[13px] font-medium text-ink" data-id="element-10163" data-mp="znzto7axygjr">
                        {request.direction === 'incoming' ? 'From' : 'To'} @{request.handle}
                      </p>
                      <p className="mt-0.5 text-[12px] leading-5 text-ink-muted" data-id="element-10164" data-mp="qoczo6kjkd0q">{request.reason}</p>
                      <p className="cz-num mt-0.5 text-[12px] text-ink-subtle" data-id="element-10165" data-mp="loqlnuww2sqv">{request.at}</p>
                    </div>
                    <span className="cz-num shrink-0 text-[13px] font-semibold text-ink" data-id="element-10166" data-mp="gqoradu0j703">
                      {naira(request.amountNaira)}
                    </span>
                  </div>
                  <div className="mt-2 flex items-center gap-2" data-id="element-10167" data-mp="2b2wueeghmu6y">
                    <Badge tone={request.state === 'settled' ? 'ok' : request.state === 'declined' ? 'warn' : 'neutral'} data-id="element-10168" data-mp="272mcq4jc2fc9">
                      {request.state === 'awaiting' ? 'Awaiting' : request.state === 'settled' ? 'Settled' : 'Declined'}
                    </Badge>
                    {request.direction === 'incoming' && request.state === 'awaiting' && <Button size="sm" variant="secondary" disabled={!isLive('social_payments')} data-id="element-10169" data-mp="n6ynb5mxo5jn">
                        Pay
                      </Button>}
                  </div>
                </li>)}
            </ul>
            <div className="border-t border-line px-5 py-3" data-id="element-10170" data-mp="j3smwemi58l3">
              <p className="cz-num text-[12px] text-ink-muted" data-id="element-10171" data-mp="j6euufdb1q3u">
                {incoming.length} request{incoming.length === 1 ? '' : 's'} awaiting you ·{' '}
                {naira(incoming.reduce((total, request) => total + request.amountNaira, 0))}
              </p>
            </div>
          </Panel>
        </div>}

      {tab === 'Bills' && <BillsPanel data-id="element-10172" data-mp="2rb5k753fa9cj" />}

      {tab === 'Capabilities' && <div className="space-y-6" data-id="element-10173" data-mp="35bygnvdabeia">
          <Panel title="Financial capability register" description="The same register the platform reads everywhere else, so a capability cannot look live here and be gated in the product." bodyClassName="p-0" data-id="element-10174" data-mp="35c85vgqi9hmx">
            <ul className="divide-y divide-line" data-id="element-10175" data-mp="2ckyelpyikkt9">
              {financialFeatures.map(feature => <li key={feature.key} className="px-5 py-4" data-id="element-10176" data-mp="2md42oolnai36">
                  <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2" data-id="element-10177" data-mp="2meozkkvfmd47">
                    <div className="min-w-0" data-id="element-10178" data-mp="2hkt3xp2n52c7">
                      <p className="text-body font-semibold text-ink" data-id="element-10179" data-mp="1twjkuuwtasor">{feature.label}</p>
                      <p className="mt-0.5 text-body-sm text-ink-muted" data-id="element-10180" data-mp="1oxq7d2hiz1ol">{feature.domain}</p>
                    </div>
                    <FeatureStatusBadge status={feature.status} data-id="element-10181" data-mp="13ex2n9m3n4r9" />
                  </div>
                  <p className="mt-2 max-w-[74ch] text-body-sm text-ink" data-id="element-10182" data-mp="18d8chkce9f9h">{feature.note}</p>
                  {feature.dependency !== 'none' && <p className="mt-1 text-caption font-medium text-warn-700" data-id="element-10183" data-mp="1dbrw3e3imeyo">
                      {dependencyLabel(feature.dependency)}
                    </p>}
                </li>)}
            </ul>
          </Panel>

          <Panel title="Who performs what" description="Campuzon records entitlement. Movement of value belongs to a licensed party." bodyClassName="p-0" data-id="element-10184" data-mp="1rc2r8tbuyfpn">
            <ul className="divide-y divide-line" data-id="element-10185" data-mp="211w2ft838std">
              {capabilityGates.map(gate => <li key={gate.id} className="px-5 py-4" data-id="element-10186" data-mp="261icxuwj7uyh">
                  <p className="text-body font-semibold text-ink" data-id="element-10187" data-mp="26339tr6bjpzi">{gate.capability}</p>
                  <p className="mt-1 max-w-[74ch] text-body-sm text-ink-muted" data-id="element-10188" data-mp="2a3dduyajw6bq">{gate.note}</p>
                  <p className="mt-1 text-caption text-ink-subtle" data-id="element-10189" data-mp="3umfuy8d4866v">
                    Performed by: {gate.performedBy}
                  </p>
                </li>)}
            </ul>
          </Panel>
        </div>}
    </div>;
}